/* eslint-disable max-len, react/require-default-props */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import {
  useDataModel, params, string, number, shape, arrayOf, bool, extend, customType
} from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { PriceProject } from './partials/PriceProject';
import Sku from '../models/Sku';
import { PriceSimple } from './partials/PriceSimple';
import { PriceDetailed } from './partials/PriceDetailed';
import { PriceMinimal } from './partials/PriceMinimal';
import { PreferredPricingBadge } from './partials/PreferredPricingBadge';
import { ExchangePricingBadge } from './partials/ExchangePricingBadge';
import helpers, { EXCHANGE_PROMOTION_TAG } from '../helpers/price-helper';
import { PromoCountdownTimer } from './partials/subcomponents/countdownTimer/PromoCountdownTimer';
import { LAYOUT_TYPES, isItemOnClearance, isRestrictedStorefrontItem } from '../utils/utils';
import { ClearancePricingBadge } from './partials/ClearancePricingBadge';
import { FEATURE_SWITCHES } from '../utils/constants';

const isValidProps = (props) => {
  const { itemId, product: propProductData } = props;
  const propPricingData = propProductData?.pricing;
  return Boolean(propPricingData || /^\d{9}$/.test(itemId));
};

const isValidData = (sku, hideForProductTypes, hideWhenZero) => {
  const { _product, pricing } = sku;
  const { wasPrice, nowPrice } = pricing;
  const hasZeroPrice = nowPrice === 0 && wasPrice === 0;

  if (hideWhenZero && hasZeroPrice) {
    return false;
  }

  return Boolean(
    _product && !(hideForProductTypes.includes(_product.info?.productSubType?.name))
  );
};

const Price = (props) => {
  const {
    basic = false,
    disableRangePricing = false,
    displayEachUom = false,
    displayDarkTheme = false,
    hideBadge = false,
    hideBulkPrice = false,
    hideLimitPerOrder = false,
    hidePromotions = false,
    hideSavingsText = false,
    hideRebates = false,
    hideForProductTypes = [],
    itemId,
    large = true,
    type = '',
    omitDetails = false,
    showPreferredPricingBadge = false,
    showPreferredPricingBadgeToolTip = false,
    showProjectPrice = false,
    product: propProductData = null,
    priceBadge = null,
    quantity = 1,
    skip = false,
    storeId: storeIdProp = null,
    zipCode: zipCodeProp = null,

    clsRemediation = {
      placeholders: false,
      preservePlaceholders: false
    },

    hideWhenZero = false,
    isCartPrice = false,
    isListView = false,
    promoId = '',
    stackWasPrice = false,
    isSignInToolTip = false,
    availabilityData = null,
    priceAlignEnd = false,
    stackWasWithUnitPrice = false
  } = props;
  const { channel, deliveryZip } = useContext(ExperienceContext);
  const {
    storeId: defaultStoreId,
    storeZip: defaultStoreZip,
    isLocalized,
    storeName,
    membershipInformation
  } = useStore();
  const {
    placeholders,
    preservePlaceholders
  } = clsRemediation || {};

  const { isExchangeCustomer } = useThdCustomer() || {};

  const { configuratorPricing } = useLifeCycleEventBus('configurator.configurator_pricing_event');
  const { sqFtCoverage } = useLifeCycleEventBus('flooringPicker.flooringPicker_pricing_event');
  const { toggleOn: flooringPickerToggleOn } = useLifeCycleEventBus('flooringPicker.flooringPicker_toggle', true);
  const isHideLimitedStockClearanceEnabled = useConfigService(FEATURE_SWITCHES.isHideLimitedStockClearanceEnabled) || false;
  const useNewMapPolicy = useConfigService(FEATURE_SWITCHES.useNewMapPolicy) || false;
  const isPlpMessagingEnabled = useConfigService(FEATURE_SWITCHES.isPlpMessagingEnabled) || false;
  const isDailyDealsEnabled = useConfigService(FEATURE_SWITCHES.isDailyDealsEnabled) || false;
  const isStrikeThroughPricingDisclaimerEnabled = useConfigService(FEATURE_SWITCHES.isStrikeThroughPricingDisclaimerEnabled) || false;
  const isMapSpecialPriceViolation = useConfigService(FEATURE_SWITCHES.isMapSpecialPriceViolation) || false;

  const layout = helpers.getLayout(type, large, flooringPickerToggleOn, showProjectPrice);
  const hasValidProps = isValidProps(props);
  const align = helpers.getAlignment(channel, layout);
  const zipCode = zipCodeProp || deliveryZip || defaultStoreZip;
  const storeId = storeIdProp || defaultStoreId;
  const { data, loading } = useDataModel('product', {
    ssr: !skip,
    skip: !hasValidProps || Boolean(propProductData),
    variables: {
      itemId,
      storeId,
      loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership
    }
  });

  const [hasDataFullyLoaded, setHasDataFullyLoaded] = useState(false);
  useEffect(() => {
    setHasDataFullyLoaded(!loading && isLocalized);
  }, [loading, isLocalized]);

  // use configurator pricing, fallback to product pricing
  const productData = configuratorPricing?.itemId === itemId
    ? configuratorPricing : (propProductData || data?.product);

  const disableRangePrice = configuratorPricing?.itemId === itemId
    ? configuratorPricing?.disableRangePricing : disableRangePricing;

  const priceOmitDetails = configuratorPricing?.itemId === itemId ? configuratorPricing?.omitDetails : omitDetails;

  const skuModel = new Sku(productData, sqFtCoverage);

  const hasValidData = isValidData(skuModel, hideForProductTypes, hideWhenZero);

  const isPreferredPricing = (showPreferredPricingBadge && skuModel.preferredPriceFlag)
    || productData?.pricing?.showPreferredPricingBadge;
  const isPreferredPricingTooltip = showPreferredPricingBadgeToolTip
    || productData?.pricing?.showPreferredPricingBadgeToolTip;
  const isExchangePromotion = productData?.pricing?.promotion?.promotionTag === EXCHANGE_PROMOTION_TAG
    || skuModel?._product?.pricing?.showExchangePricing || productData?.pricing?.promotionalAdjustments?.some((promo) => promo?.promoId === promoId);
  const isDailySpecial = typeof productData?.pricing?.dates?.start === 'string'
    && typeof productData?.pricing?.dates?.end === 'string';

  const store = helpers.getLocalStoreName(productData) || storeName;
  const isClearanceItem = isItemOnClearance(productData, isHideLimitedStockClearanceEnabled);

  // Hide Price component for restricted products for exchange users
  if (isExchangeCustomer && isRestrictedStorefrontItem(productData, availabilityData)) {
    return null;
  }

  // (1) invalidate props
  if (!hasValidProps) {
    return null;
  }

  // (2) invalidate data
  if (!hasValidData) {
    if (!placeholders) {
      return null;
    }

    if (hasDataFullyLoaded && !preservePlaceholders) {
      return null;
    }
  }

  const priceClasses = classNames({
    'sui-invisible': !hasValidData || !hasDataFullyLoaded
  });

  const onlinePriceTextClasses = classNames('sui-font-normal sui-leading-4 sui-pr-[5px] sui-pt-2 sui-mb-2', {
    'sui-mb-2': layout === LAYOUT_TYPES.PROJECT
  });

  const showOnlinePriceText = () => {
    return (
      <p className={onlinePriceTextClasses}>Online Price</p>
    );
  };

  return (
    <div className={priceClasses} data-component="Price">
      {!isCartPrice && isClearanceItem
        && showOnlinePriceText()}
      {isPreferredPricing && (
        <PreferredPricingBadge
          channel={channel}
          type={layout}
          showPreferredPricingBadgeToolTip={isPreferredPricingTooltip}
          align={align}
          isListView={isListView}
        />
      )}
      {isExchangePromotion && (
        <ExchangePricingBadge
          padLeft={layout === LAYOUT_TYPES.DETAILED}
          align={align}
        />
      )}
      {(layout === LAYOUT_TYPES.DETAILED) && (
        <PriceDetailed
          channel={channel}
          disableRangePricing={disableRangePrice}
          showStartingAt={configuratorPricing?.showStartingAt}
          displayEachUom={displayEachUom}
          hideBadge={hideBadge}
          hideBulkPrice={hideBulkPrice}
          hideLimitPerOrder={hideLimitPerOrder}
          hideRebates={hideRebates}
          hideSavingsText={hideSavingsText}
          omitDetails={omitDetails}
          skuModel={skuModel}
          storeId={storeId}
          zipCode={zipCode}
          useNewMapPolicy={useNewMapPolicy}
          isMapSpecialPriceViolation={isMapSpecialPriceViolation}
          stackWasPrice={stackWasPrice}
          isSignInToolTip={isSignInToolTip}
          isDailyDealsEnabled={isDailyDealsEnabled}
          isStrikeThroughPricingDisclaimerEnabled={isStrikeThroughPricingDisclaimerEnabled}
        />
      )}
      {(layout === LAYOUT_TYPES.SIMPLE) && (
        <PriceSimple
          basic={basic}
          channel={channel}
          disableRangePricing={disableRangePricing}
          displayEachUom={displayEachUom}
          displayDarkTheme={displayDarkTheme}
          hideBadge={hideBadge}
          hideBulkPrice={hideBulkPrice}
          hideLimitPerOrder={hideLimitPerOrder}
          hidePromotions={hidePromotions}
          hideRebates={hideRebates}
          hideSavingsText={hideSavingsText}
          omitDetails={priceOmitDetails}
          priceBadge={priceBadge}
          skuModel={skuModel}
          zipCode={zipCode}
          useNewMapPolicy={useNewMapPolicy}
          isMapSpecialPriceViolation={isMapSpecialPriceViolation}
          stackWasPrice={stackWasPrice}
          isSignInToolTip={isSignInToolTip}
          isPlpMessagingEnabled={isPlpMessagingEnabled}
          priceAlignEnd={priceAlignEnd}
          isDailyDealsEnabled={isDailyDealsEnabled}
          stackWasWithUnitPrice={stackWasWithUnitPrice}
        />
      )}
      {(layout === LAYOUT_TYPES.MINIMAL) && (
        <PriceMinimal
          skuModel={skuModel}
          zipCode={zipCode}
          quantity={quantity}
          align={align}
          useNewMapPolicy={useNewMapPolicy}
          isSignInToolTip={isSignInToolTip}
        />
      )}
      {(layout === LAYOUT_TYPES.PROJECT) && (
        // TODO: Remove this PriceProject and splice it in to Simple and Detailed
        (<PriceProject
          type={type}
          large={large}
          channel={channel}
          disableRangePricing={disableRangePrice}
          showStartingAt={configuratorPricing?.showStartingAt}
          displayEachUom={displayEachUom}
          hideBadge={hideBadge}
          hideBulkPrice={hideBulkPrice}
          hideLimitPerOrder={hideLimitPerOrder}
          hideRebates={hideRebates}
          hideSavingsText={hideSavingsText}
          omitDetails={omitDetails}
          skuModel={skuModel}
          storeId={storeId}
          zipCode={zipCode}
          useNewMapPolicy={useNewMapPolicy}
          isMapSpecialPriceViolation={isMapSpecialPriceViolation}
          isDailyDealsEnabled={isDailyDealsEnabled}
        />)
      )}
      {isDailySpecial && <PromoCountdownTimer end={productData?.pricing?.dates?.end} align={align} />}
      {!isCartPrice && isClearanceItem
        && (
          <ClearancePricingBadge
            storeName={store}
            originalPrice={productData?.pricing?.original
              ? productData?.pricing?.original : productData?.pricing?.value}
            clearanceValue={productData?.pricing?.clearance?.value}
            dollarOff={productData?.pricing?.clearance?.dollarOff}
            percentageOff={productData?.pricing?.clearance?.percentageOff}
            sku={productData?.itemId}
            unitsClearancePrice={productData?.pricing?.clearance?.unitsClearancePrice}
            caseUnitOfMeasure={productData?.pricing?.alternate?.unit?.caseUnitOfMeasure}
            unitsOriginalPrice={productData?.pricing?.alternate?.unit?.unitsOriginalPrice
              || productData?.pricing?.alternate?.unit?.value}
            alternatePriceDisplay={productData?.pricing?.alternatePriceDisplay}
          />
        )}
    </div>
  );
};

Price.displayName = 'Price';

Price.propTypes = {
  /**
   * To show the basic price format
   */
  basic: PropTypes.bool,
  /**
   * these props control CLS by manipulating placeholder logic
   */
  clsRemediation: PropTypes.shape({
    placeholders: PropTypes.bool,
    preservePlaceholders: PropTypes.bool
  }),
  /**
   * disables the display of range pricing often seen in generic sku product pods
   */
  disableRangePricing: PropTypes.bool,
  /**
   * disables the "each" uom treatment
   */
  displayEachUom: PropTypes.bool,
  /**
   * uses dark mode css
   */
  displayDarkTheme: PropTypes.bool,
  /**
   * suppresses the display of badges, such as bulk pricing
   */
  hideBadge: PropTypes.bool,
  /**
   * disables bulk pricing messaging
   */
  hideBulkPrice: PropTypes.bool,
  /**
   * suppresses the display of limit per order text
   */
  hideLimitPerOrder: PropTypes.bool,
  /**
   * disables display of the short description for promotions
   */
  hidePromotions: PropTypes.bool,
  /**
   * Hide Savings Text
   */
  hideSavingsText: PropTypes.bool,
  /**
   * Hide Rebate Messaging and prevent Rebate Calls
   */
  hideRebates: PropTypes.bool,
  /**
   * specifies product types for which to hide the price. i.e. Merchandise, major_appliances, etc
   */
  hideForProductTypes: PropTypes.arrayOf(PropTypes.string),
  /**
   * OmsId for the item
   */
  itemId: PropTypes.string.isRequired,
  /**
   * Determines whether to display larger or smaller Price Component Variant
   */
  large: PropTypes.bool,
  /**
   * to display only a very simplified view of the price
   */
  omitDetails: PropTypes.bool,
  /**
   * "product" query data. must contain pricing information
   */
  product: PropTypes.shape({
    pricing: PropTypes.shape({}).isRequired
  }),
  /**
   * to show preferred pricing badge for B2B customers when preferred pricing is available
   */
  showPreferredPricingBadge: PropTypes.bool,
  /**
   * to show the tooltip that describes preferred pricing
   */
  showPreferredPricingBadgeToolTip: PropTypes.bool,
  /**
   * to show an esitmated price for your project based on areas saved in PIPCalculator/FlooringCalculator
   */
  showProjectPrice: PropTypes.bool,
  /**
   * forces SSR to skip requesting price data
   */
  skip: PropTypes.bool,
  /**
   * to override the customers localized store when requesting pricing info
   */
  storeId: PropTypes.string,
  /**
   * 'detailed': PIP style treatment, 'simple': PLP style, 'minimal': Cart style
  */
  type: PropTypes.oneOf([...Object.values(LAYOUT_TYPES), '']),
  /**
  * zipCode is used for ecorebates. Providing it as prop overrides the customer's selected deliveryZip
  */
  zipCode: PropTypes.string,
  /**
   * hides the price when it is 0. Will leverage clsRemediation when provided
   */
  hideWhenZero: PropTypes.bool,
  /**
   * hides the price when it is 0. Will leverage clsRemediation when provided
   */
  quantity: PropTypes.number,
  /**
   * removes the padding from PriceDetailed, PriceSimple and removes the left margin from large currency symbol
   */
  isCartPrice: PropTypes.bool,
  isListView: PropTypes.bool,
  promoId: PropTypes.string,
  priceBadge: PropTypes.string,
  /**
   * Dynamically allows the was-price to wrap and Stack under main price if set to true.
   * Often used for prices with limited space (ie in the recs product pod)
   */
  stackWasPrice: PropTypes.bool,
  /**
   * Shows the sign-in tooltip for pricing
  */
  isSignInToolTip: PropTypes.bool,
  /**
    * "checkAvailability" query data
    */
  availabilityData: PropTypes.shape({
    checkAvailability: PropTypes.shape({})
  }),
  priceAlignEnd: PropTypes.bool,
  stackWasWithUnitPrice: PropTypes.bool
};

export const PriceClearanceDataModel = {
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    itemId: string(),
    dataSources: string(),
    pricing: params({ storeId: string() }).shape({
      clearance: shape({
        value: number({ float: true }),
        dollarOff: number({ float: true }),
        percentageOff: number({ float: true }),
        unitsClearancePrice: number({ float: true })
      })
    })
  })
};

Price.dataModel = extend({
  product: params({
    itemId: string().isRequired(),
    dataSource: string(),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: string(),
      programTiers: arrayOf(shape({
        tier: string(),
        program: string()
      }))
    })
  }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      itemId: string()
    }),
    info: shape({
      hidePrice: bool(),
      ecoRebate: bool(),
      quantityLimit: number(),
      categoryHierarchy: arrayOf(string()),
      sskMin: string(),
      sskMax: string(),
      unitOfMeasureCoverage: string(),
      wasMaxPriceRange: number({ float: true }),
      wasMinPriceRange: number({ float: true }),
      productSubType: shape({
        name: string()
      })
    }),
    pricing: params({
      storeId: string(),
      isBrandPricingPolicyCompliant: bool()
    }).shape({
      alternatePriceDisplay: bool(),
      alternate: shape({
        bulk: shape({
          pricePerUnit: number({ float: true }),
          thresholdQuantity: number(),
          value: number({ float: true })
        }),
        unit: {
          caseUnitOfMeasure: string(),
          unitsOriginalPrice: number({ float: true }),
          unitsPerCase: number({ float: true }),
          value: number({ float: true })
        }
      }),
      original: number({ float: true }),
      mapAboveOriginalPrice: bool(),
      mapDetail: shape({
        percentageOff: number({ float: true }),
        dollarOff: number({ float: true }),
        effectiveMap: number({ float: true }),
        mapPolicy: string(),
        mapOriginalPriceViolation: bool(),
        mapSpecialPriceViolation: bool(),
      }),
      message: string(),
      preferredPriceFlag: bool(),
      promotion: shape({
        type: string(),
        description: shape({
          shortDesc: string(),
          longDesc: string()
        }),
        dollarOff: number({ float: true }),
        percentageOff: number({ float: true }),
        promotionTag: string(),
        savingsCenter: string(),
        savingsCenterPromos: string(),
        specialBuySavings: string(),
        specialBuyDollarOff: number({ float: true }),
        specialBuyPercentageOff: number({ float: true })
      }),
      specialBuy: number({ float: true }),
      unitOfMeasure: string(),
      value: number({ float: true })
    }),
    taxonomy: shape({
      breadCrumbs: arrayOf(shape({
        label: string()
      }))
    })
  })
});

export default Price;
